import _ from 'lodash';
import moment from 'moment';
import constants from '$$helpers/constants';
import {
  DEFAULT_VALUES_BY_COLUMN_TYPE,
  getTemplateForCheckbox,
  getTemplateForColorSelect,
  getTemplateForListMultiSelect,
  getTemplateForListResources,
  getTemplateForListSelect,
  getTemplateForTags,
  getTemplateForText,
  prepareOptionsForResources,
} from '../helpers/userCustomColumns';
import BaselineGrid from '../baselines/grid';
import dateHelper from '$$dateFormatsHelper';
import numbersHelper from '$$helpers/numbers';

class UserCustomColumns {
  #instance = null;

  init(instance) {
    this.#instance = instance;
  }

  getUserCustomValue(task, columnId) {
    const ganttID = task.gantt_id || this.#instance.config.gantt_id;

    if (!ganttID) {
      return '';
    }

    const column = this.#instance.ganttDataApi.getCustomColumnById(columnId);

    return this.getValueForUserColumn(task, column);
  }

  getValueForUserColumn(task, column) {
    const taskTypesForColumn = ['project', 'task', 'milestone'];

    const result = {
      text: '',
      value: null,
      toExcel: '',
    };

    if (!column) {
      return result;
    }

    const totalEstimateTaskLevel = this.#instance.config.multiview ? 2 : 1;

    if (!_.includes(taskTypesForColumn, task.type) || (task.$level < totalEstimateTaskLevel && column.type !== constants.CUSTOM_COLUMNS_TYPES.number.id)) { // task.$level === 0
      return result;
    }

    const taskCustomValues = this.#instance.ganttDataApi.getCustomColumnValuesByParams(task.gantt_id, task.id, column.id);

    return this.prepareColumnValue(result, task, column, taskCustomValues);
  }

  prepareColumnValue(result, task, column, taskCustomValues, isBaseline) {
    if (column.options && column.options.length) {
      const taskCustomValueIds = (taskCustomValues || []).map(value => value.task_value);
      const defaultOption = _.find(column.options, opt => opt.is_default);
      const findOption = _.filter(column.options, opt => taskCustomValueIds.includes(opt.id));

      if (findOption.length) {
        result.text = findOption.map(option => option.value).join('');
        result.value = findOption.map(option => option.id).join(',');
      } else if (defaultOption) {
        result.text = defaultOption.value;
        result.value = `${defaultOption.id}`;
      }

      result.toExcel = result.text;

      result.text = _.escape(result.text);

      if (column.type === constants.CUSTOM_COLUMNS_TYPES.color.id) {
        result.text = getTemplateForColorSelect(result.text);
      }

      if (column.type === constants.CUSTOM_COLUMNS_TYPES.select.id) {
        result.text = getTemplateForListSelect(result.text);
      }

      if (column.type === constants.CUSTOM_COLUMNS_TYPES.multiselect.id) {
        const multiSelectText = findOption.map(option => option.value).join(', ');

        result.text = getTemplateForListMultiSelect(multiSelectText);
        result.toExcel = multiSelectText;
        result.preview = findOption;
      }

      if (column.type === constants.CUSTOM_COLUMNS_TYPES.tags.id) {
        result.text = getTemplateForTags(findOption);
        result.toExcel = findOption;
        result.preview = findOption;
      }
      if (column.type === constants.CUSTOM_COLUMNS_TYPES.resources.id) {
        const items = this.#instance.ganttDataApi.getResourcesByProjectIdForUI(task.gantt_id);
        const resources = prepareOptionsForResources(findOption, items);

        result.text = getTemplateForListResources(resources);
        result.toExcel = resources.map(option => option.value).join(', ');
        result.preview = resources;
      }
    } else {
      let value = taskCustomValues.length ? taskCustomValues.map(val => val.task_value).join('') : DEFAULT_VALUES_BY_COLUMN_TYPE[column.type];

      if (task.type === this.#instance.config.types.project && column.type === constants.CUSTOM_COLUMNS_TYPES.number.id) {
        if (column.sub_type === constants.CUSTOM_COLUMNS_SUBTYPES.sum.id) {
          value = this.getCalcForParent(task, column, isBaseline, true, null);
        }

        if (column.sub_type === constants.CUSTOM_COLUMNS_SUBTYPES.average.id) {
          value = this.getCalcForParent(task, column, isBaseline, false, null);
        }
      }

      result.value = value;
      result.text = value;
      result.toExcel = value;

      if (column.type === constants.CUSTOM_COLUMNS_TYPES.checkbox.id) {
        result.text = getTemplateForCheckbox(result.text);
        result.value = +value;
        result.toExcel = result.value ? __('common_on') : __('common_off');
      }

      if (column.type === constants.CUSTOM_COLUMNS_TYPES.date.id) {
        const date = value && value !== '0' ? moment(value).toDate() : DEFAULT_VALUES_BY_COLUMN_TYPE[column.type];
        const dateString = date ? this.#instance.date.date_to_str(dateHelper.getDateFormat())(date) : DEFAULT_VALUES_BY_COLUMN_TYPE[column.type];

        result.value = date;
        result.text = dateString;
        result.toExcel = date;
      }

      if (column.type === constants.CUSTOM_COLUMNS_TYPES.number.id) {
        result.toExcel = (result.text || 0) * 1;
        result.text = numbersHelper.getFormatted(+result.text);
      }

      if (column.type === constants.CUSTOM_COLUMNS_TYPES.text.id) {
        result.text = getTemplateForText(result.text);
      }
    }

    return result;
  }

  getCalcForParent(task, column, isBaseline, sumMode, valuesMap) {
    const children = this.#instance.getChildren(task.id);
    let projectColumnsValues;
    let parentSum = 0;
    let count = 0;
    let parentAverage = 0;
    let result = 0;
    let taskCustomValuesMap = valuesMap;

    // if (!valuesMap) {
    //   if (isBaseline) {
    //     taskCustomValuesMap = BaselineGrid.getUserCustomValuesMap();
    //   } else {
    //     taskCustomValuesMap = this.#instance.ganttDataApi.getUserCustomValuesMapByProjectId(task.gantt_id);
    //   }
    // }

    if (isBaseline) {
      projectColumnsValues = BaselineGrid.getUserCustomValuesMap();
    } else {
      projectColumnsValues = this.#instance.ganttDataApi.getProjectColumnsDataByProjectId(task.gantt_id)?.values || {};
    }

    _.each(children, child => {
      const task = this.#instance.getTask(child);

      if (task.type === this.#instance.config.types.task || task.type === this.#instance.config.types.milestone) {
        let taskCustomValue = 0;
        const valueObj = projectColumnsValues[task.id] ? projectColumnsValues[task.id].find(o => o.column_id === column.id) : {};

        // taskCustomValue = taskCustomValuesMap[`${task.id}-${column.id}`] ? parseFloat(taskCustomValuesMap[`${task.id}-${column.id}`], 10) : 0;
        taskCustomValue = parseFloat(valueObj?.task_value, 10) || 0;
        parentSum += taskCustomValue;

        count++;
      }
      if (task.type === this.#instance.config.types.project) {
        parentSum += this.getCalcForParent(task, column, isBaseline, sumMode, taskCustomValuesMap);
        count++;
      }
    });

    parentAverage = parentSum / count;

    result = sumMode ? parentSum : !isNaN(parentAverage) ? parentAverage : 0;

    return Math.round(result * 100) / 100;
  }
}

export default new UserCustomColumns();
